/*
 * Copyright (C) 2021 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {AlertManagerContext} from '@instructure/platform-alerts'
import {AUTO_MARK_AS_READ_DELAY, SearchContext} from '../../utils/constants'
import {Discussion} from '../../../graphql/Discussion'
import {DiscussionEntry} from '../../../graphql/DiscussionEntry'
import {DiscussionThreadContainer} from '../DiscussionThreadContainer/DiscussionThreadContainer'
import {useScope as createI18nScope} from '@canvas/i18n'
import {
  updateDiscussionTopicEntryCounts,
  updateDiscussionEntryRootEntryCounts,
} from '../../utils/index'
import PropTypes from 'prop-types'
import React, {useContext, useEffect, useState} from 'react'
import {SearchResultsCount} from '../../components/SearchResultsCount/SearchResultsCount'
import {ThreadPagination} from '../../components/ThreadPagination/ThreadPagination'
import {UPDATE_DISCUSSION_ENTRIES_READ_STATE} from '../../../graphql/Mutations'
import {useMutation} from '@apollo/client'
import {View} from '@instructure/ui-view'

const I18n = createI18nScope('discussion_topics_post')

// @ts-expect-error TS7006 (typescriptify)
export const DiscussionTopicRepliesContainer = props => {
  const {setOnFailure, setOnSuccess} = useContext(AlertManagerContext)
  // @ts-expect-error TS2339 (typescriptify)
  const {filter, searchTerm, setPageNumber, setSearchPageNumber, setExpandedThreads} =
    useContext(SearchContext)

  const [discussionEntriesToUpdate, setDiscussionEntriesToUpdate] = useState(new Set())

  // @ts-expect-error TS7006 (typescriptify)
  const updateCache = (cache, result) => {
    updateDiscussionTopicEntryCounts(cache, props.discussionTopic.id, {
      unreadCountChange: -result?.data?.updateDiscussionEntriesReadState?.discussionEntries?.length,
    })

    // update each root discussionEntry in cache
    // @ts-expect-error TS7006 (typescriptify)
    result?.data?.updateDiscussionEntriesReadState?.discussionEntries?.forEach(discussionEntry => {
      const discussionEntryOptions = {
        id: btoa('DiscussionEntry-' + discussionEntry._id),
        fragment: DiscussionEntry.fragment,
        fragmentName: 'DiscussionEntry',
      }

      const data = JSON.parse(JSON.stringify(cache.readFragment(discussionEntryOptions)))

      data.entryParticipant.read = discussionEntry.entryParticipant.read

      cache.writeFragment({
        ...discussionEntryOptions,
        data,
      })

      if (discussionEntry.rootEntryId && !discussionEntry.deleted) {
        const discussionUnreadCountchange = discussionEntry.entryParticipant.read ? -1 : 1
        updateDiscussionEntryRootEntryCounts(cache, discussionEntry, discussionUnreadCountchange)
      }
    })
  }

  const [updateDiscussionEntriesReadState] = useMutation(UPDATE_DISCUSSION_ENTRIES_READ_STATE, {
    update: updateCache,
    onCompleted: () => {
      setOnSuccess(I18n.t('The replies were successfully updated'))
    },
    onError: error => {
      if (!error.networkError) {
        setOnFailure(I18n.t('There was an unexpected error while marking replies as read'))
      }
    },
  })

  useEffect(() => {
    if (discussionEntriesToUpdate.size > 0 && !searchTerm) {
      const interval = setInterval(() => {
        const entryIds = Array.from(discussionEntriesToUpdate)
        const entries = JSON.parse(
          JSON.stringify(
            props.discussionTopic.discussionEntriesConnection.nodes.filter(
              // @ts-expect-error TS7006 (typescriptify)
              entry => entryIds.includes(entry._id) && entry.entryParticipant?.read === false,
            ),
          ),
        )
        // @ts-expect-error TS7006 (typescriptify)
        entries.forEach(entry => (entry.entryParticipant.read = true))
        setDiscussionEntriesToUpdate(new Set())
        updateDiscussionEntriesReadState({
          variables: {
            discussionEntryIds: entryIds,
            read: true,
          },
        })
      }, AUTO_MARK_AS_READ_DELAY)

      return () => clearInterval(interval)
    }
  }, [
    discussionEntriesToUpdate,
    props.discussionTopic.discussionEntriesConnection.nodes,
    updateDiscussionEntriesReadState,
    filter,
    searchTerm,
  ])

  // @ts-expect-error TS7006 (typescriptify)
  const markAsRead = entryId => {
    if (!discussionEntriesToUpdate.has(entryId)) {
      const entries = Array.from(discussionEntriesToUpdate)
      setDiscussionEntriesToUpdate(new Set([...entries, entryId]))
    }
  }

  // @ts-expect-error TS7006 (typescriptify)
  const setPage = pageNum => {
    // @ts-expect-error TS2554 (typescriptify)
    setExpandedThreads([])
    // @ts-expect-error TS2554 (typescriptify)
    props.isSearchResults ? setSearchPageNumber(pageNum) : setPageNumber(pageNum)
  }

  return (
    <View
      as="div"
      padding="mediumSmall mediumSmall large mediumSmall"
      data-testid="discussion-root-entry-container"
    >
      {searchTerm && <SearchResultsCount resultsFound={props.discussionTopic.searchEntryCount} />}
      {/* @ts-expect-error TS7006 (typescriptify) */}
      {props.discussionTopic.discussionEntriesConnection.nodes.map(thread => {
        return (
          <DiscussionThreadContainer
            key={`discussion-thread-${thread.id}`}
            discussionEntry={thread}
            discussionTopic={props.discussionTopic}
            markAsRead={markAsRead}
            onOpenSplitView={props.onOpenSplitView}
            goToTopic={props.goToTopic}
            highlightEntryId={props.highlightEntryId}
            setHighlightEntryId={props.setHighlightEntryId}
            userSplitScreenPreference={props.userSplitScreenPreference}
            refetchDiscussionEntries={props.refetchDiscussionEntries}
          />
        )
      })}
      {props.discussionTopic.entriesTotalPages > 1 && (
        <ThreadPagination
          setPage={setPage}
          selectedPage={Math.ceil(
            // @ts-expect-error TS2362 (typescriptify)
            atob(props.discussionTopic.discussionEntriesConnection.pageInfo.startCursor) /
              // @ts-expect-error TS2339 (typescriptify)
              ENV.per_page,
          )}
          totalPages={props.discussionTopic.entriesTotalPages}
        />
      )}
    </View>
  )
}

DiscussionTopicRepliesContainer.propTypes = {
  discussionTopic: Discussion.shape,
  onOpenSplitView: PropTypes.func,
  goToTopic: PropTypes.func,
  highlightEntryId: PropTypes.string,
  isSearchResults: PropTypes.bool,
  setHighlightEntryId: PropTypes.func,
  userSplitScreenPreference: PropTypes.bool,
  refetchDiscussionEntries: PropTypes.func,
}

export default DiscussionTopicRepliesContainer
